/**
 * SearchPanel - collapsible filter panel.
 *
 * A bar that is always visible (title, optional meta slot, filter chips while
 * collapsed, an active-filter badge and a chevron) over a body that expands and
 * collapses. Used by Browse Properties and Advanced Search.
 *
 * The toggle is a stretched transparent overlay covering the whole bar rather
 * than a wrapper around it: the chips carry their own dismiss buttons, and a
 * <button> may not contain another button.
 */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import './SearchPanel.css';

// Matches the body's expand transition; focus moves in only once it settles.
const FOCUS_DELAY_MS = 350;

const SearchPanel = forwardRef(function SearchPanel(
  {
    title,
    regionLabel,
    idPrefix = 'search-panel',
    defaultOpen = true,
    meta = null,
    chips = [],
    onChipDismiss,
    badgeCount = 0,
    bodyLabel = 'Search form',
    scrollBody = true,
    children,
  },
  ref
) {
  const [isExpanded, setIsExpanded] = useState(defaultOpen);

  const bodyRef = useRef(null);
  const headerRef = useRef(null);
  const openFocusTimerRef = useRef(null);

  const bodyId = `${idPrefix}-body`;
  const titleId = `${idPrefix}-title`;

  /**
   * Collapse the panel and hand focus back to the header button. The body is
   * aria-hidden while collapsed, so focus must not be left inside it.
   */
  const close = useCallback(() => {
    // Cancel a pending open-focus hop, or it would drop focus into the body
    // after the panel has already collapsed.
    clearTimeout(openFocusTimerRef.current);
    setIsExpanded(false);
    headerRef.current?.focus();
  }, []);

  const open = useCallback(() => {
    setIsExpanded(true);
    // The ref can be gone by the time this fires if the component unmounted,
    // so re-read it inside the callback.
    clearTimeout(openFocusTimerRef.current);
    openFocusTimerRef.current = setTimeout(() => {
      const firstInput = bodyRef.current?.querySelector('input, button, select');
      if (firstInput) firstInput.focus();
    }, FOCUS_DELAY_MS);
  }, []);

  useImperativeHandle(ref, () => ({ open, close, isExpanded }), [open, close, isExpanded]);

  useEffect(() => () => clearTimeout(openFocusTimerRef.current), []);

  const toggle = () => {
    if (isExpanded) {
      close();
    } else {
      open();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && isExpanded) {
      close();
    }
  };

  return (
    <div
      className={[
        'search-panel',
        isExpanded ? 'search-panel--expanded' : 'search-panel--collapsed',
        scrollBody ? '' : 'search-panel--flow',
      ].filter(Boolean).join(' ')}
      role="region"
      aria-label={regionLabel || `${title} filters`}
      onKeyDown={handleKeyDown}
    >
      <div className="search-panel__bar">
        {/* Stretched click target; draws nothing and takes its accessible name
            from the visible title. */}
        <button
          ref={headerRef}
          type="button"
          className="search-panel__header"
          onClick={toggle}
          aria-expanded={isExpanded}
          aria-controls={bodyId}
          aria-labelledby={titleId}
        />
        <span className="search-panel__icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
          </svg>
        </span>
        <span className="search-panel__title" id={titleId}>{title}</span>
        {meta}
        <span className="search-panel__chips">
          {!isExpanded && chips.map((chip) => (
            <span key={chip.key} className="filter-chip">
              {chip.label}
              <button
                type="button"
                className="filter-chip__dismiss"
                aria-label={`Remove ${chip.label} filter`}
                onClick={() => onChipDismiss && onChipDismiss(chip.key)}
              >&times;</button>
            </span>
          ))}
        </span>
        {badgeCount > 0 && (
          <span className="search-panel__badge" aria-label={`${badgeCount} active filters`}>
            {badgeCount}
          </span>
        )}
        <span className="search-panel__chevron" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 4.5l3 3 3-3"/>
          </svg>
        </span>
      </div>

      {/* `inert` is what actually takes the collapsed body out of the tab order.
          The body is hidden with `max-height: 0; overflow: hidden`, which clips
          it visually but leaves every control inside focusable -- so without
          this, Tab walks into a form the user can neither see nor hear, and
          Enter submits it. `tabIndex={-1}` only covers this container, never its
          descendants. `aria-hidden` stays as the fallback for browsers without
          `inert` support. */}
      <div
        ref={bodyRef}
        id={bodyId}
        className={`search-panel__body ${scrollBody ? '' : 'search-panel__body--flow'}`}
        role="group"
        aria-label={bodyLabel}
        inert={!isExpanded}
        aria-hidden={!isExpanded}
        tabIndex={isExpanded ? undefined : -1}
      >
        {children}
      </div>
    </div>
  );
});

export default SearchPanel;
